import { useParams, Link } from "react-router-dom";
import { getVideoById, getRelatedVideos } from "@/mocks/videos";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEO from "@/components/base/SEO";
import type { BreadcrumbItem } from "@/components/base/Breadcrumb";
import type { VideoItem } from "@/mocks/videos";

function RelatedCard({ video }: { video: VideoItem }) {
  return (
    <Link
      to={`/video/${video.id}`}
      className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
        <img
          src={video.image}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
      </div>
      <div className="flex flex-col justify-center min-w-0">
        <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-accent-500 transition-colors">
          {video.title}
        </h4>
        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-foreground-500">
          <span>{video.duration}</span>
          <span>·</span>
          <span>{video.rating}分</span>
        </div>
        <p className="text-xs text-foreground-400 mt-1 line-clamp-1">{video.description}</p>
      </div>
    </Link>
  );
}

function VideoJsonLd({ video }: { video: VideoItem }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: video.title,
    description: video.description,
    image: video.image,
    dateCreated: `${video.year}`,
    director: { "@type": "Person", name: video.director },
    actor: video.actors.map((a) => ({ "@type": "Person", name: a })),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: video.rating,
      bestRating: "10",
      ratingCount: video.views,
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.taozivideo.com" },
        { "@type": "ListItem", position: 2, name: video.categoryName, item: `https://www.taozivideo.com/category/${video.category}` },
        { "@type": "ListItem", position: 3, name: video.title },
      ],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export default function VideoPage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(Number(id));
  const relatedVideos = video ? getRelatedVideos(video.id) : [];

  if (!video) {
    return (
      <>
        <SEO
          title="视频未找到 - 桃子视频"
          description="抱歉，您要查看的视频不存在或已被移除。"
          keywords="桃子视频,影视,在线观看"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 pb-16 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">视频未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的视频不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "首页", href: "/" },
    { label: video.categoryName, href: `/category/${video.category}` },
    { label: video.title },
  ];

  return (
    <>
      <SEO
        title={`${video.title} - 桃子视频免费高清在线观看 | ${video.categoryName}`}
        description={`${video.title} - ${video.description} 桃子视频免费高清在线观看，${video.brand}提供，评分${video.rating}分。`}
        keywords={`${video.title},${video.categoryName},桃子视频,在线观看,高清影视`}
        canonicalUrl={`https://www.taozivideo.com/video/${video.id}`}
        ogType="video.movie"
        ogImage={video.image}
      />
      <VideoJsonLd video={video} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
                {/* Main Content */}
                <div className="lg:col-span-2">
                  <div className="relative rounded-xl overflow-hidden mb-6">
                    <div className="aspect-video bg-foreground-950">
                      <img
                        src={video.image}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500/90 flex items-center justify-center cursor-pointer hover:bg-accent-500 transition-colors">
                          <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {video.isHot && (
                        <span className="px-3 py-1 text-xs font-semibold bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                          热门推荐
                        </span>
                      )}
                      {video.isNew && (
                        <span className="px-3 py-1 text-xs font-semibold bg-secondary-500 text-background-50 rounded-full whitespace-nowrap">
                          最新上线
                        </span>
                      )}
                      <span className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                        {video.categoryName}
                      </span>
                      <span className="text-xs text-foreground-500">{video.brand} 出品</span>
                    </div>
                    <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                      {video.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-600 mb-4">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i>
                        <strong>{video.rating}</strong> 分
                      </span>
                      <span>{video.year}</span>
                      <span>{video.duration}</span>
                      <span>{video.views.toLocaleString()} 次播放</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {video.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-xl p-5 mb-6">
                    <h2 className="text-base font-bold text-foreground-950 mb-3">剧情简介</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                      {video.detailContent}
                    </p>
                  </div>

                  <div className="bg-background-50 rounded-xl p-5 mb-6">
                    <h2 className="text-base font-bold text-foreground-950 mb-3">演职人员</h2>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs text-foreground-500">导演</span>
                        <p className="text-sm font-medium text-foreground-900 mt-1">{video.director}</p>
                      </div>
                      <div>
                        <span className="text-xs text-foreground-500">主演</span>
                        <p className="text-sm font-medium text-foreground-900 mt-1">
                          {video.actors.join(" / ")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar - Related Videos */}
                <div className="lg:col-span-1">
                  <div className="sticky top-20">
                    <h3 className="text-base font-bold text-foreground-950 mb-4">
                      相关推荐
                    </h3>
                    <div className="space-y-2">
                      {relatedVideos.map((v) => (
                        <RelatedCard key={v.id} video={v} />
                      ))}
                    </div>
                    {relatedVideos.length === 0 && (
                      <p className="text-sm text-foreground-500">暂无相关推荐</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}